import BaseApiClient from '../BaseApiClient';
import { ListDataParams, ListDataResponse } from '../types';
import {
  PolicyInterface,
  OffPlatformPolicyInterface,
  PolicyInvoiceInterface,
  cancellationReasonInterface
} from '@interfaces/Policy.interface';
import {
  ACTIVE_POLICIES,
  CANCELLED_POLICIES,
  COOLING_OFF_POLICIES,
  IOffPlatformPoliciesStoreRequest,
  RENEWAL_POLICIES
} from './types';
import { AxiosPromise, AxiosResponse } from 'axios';

export class ApiPolicy extends BaseApiClient {
  /**
   * Gets current user
   */
  async list(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<PolicyInterface>> {
    return new Promise((resolve, reject) => {
      if (searchTerm) {
        if (!listData.filters) listData.filters = {};
        listData.filters.policyKeyword = searchTerm;
      }
      this.client
        .getList('/policies', listData)
        .then(({ data }) => {
          if (data) {
            resolve({
              items: data.data,
              pagination: {
                page: data.meta?.page,
                perPage: data.meta?.per_page,
                total: data.meta?.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  get(id: string | undefined): Promise<PolicyInterface> {
    return new Promise((resolve) => {
      this.client
        .get(`/policies/${id}`)
        .then((res) => resolve(res.data))
        .catch(console.error);
    });
  }

  updatePaidStatus(
    id: string | undefined,
    invoiceId: string | undefined,
    body: {
      paid: string;
    }
  ): Promise<PolicyInvoiceInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .put(`/policies/${id}/invoices/${invoiceId}`, body)
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }

  deletePolicy(
    id: string | undefined,
    body: {
      data: {
        policy_cancellation_reason_id: string;
        // date: string;
        // admin_fee_waived_reason: string | null;
        // cancellation_date_reason: string | null;
      };
    }
  ): AxiosPromise<true> {
    return new Promise((resolve, reject) => {
      this.client
        .delete(`/policies/${id}`, body)
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }

  sendRenewalEmail(id: string | undefined): AxiosPromise<true> {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/policies/${id}/renewal-email`)
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }

  sendWelcomeEmail(id: string | undefined): AxiosPromise<true> {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/policies/${id}/welcome-email`)
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }

  async invoices(id: string | undefined): Promise<PolicyInvoiceInterface[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/policies/${id}/invoices`)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getPolicyCountByState(state: string): Promise<number> {
    const res = await this.client.get<{ count: number }>(
      '/policies?count=1&filter[state]=' + encodeURIComponent(state)
    );
    return res.data.count;
  }

  async statistics(...policies: string[]) {
    const map: Record<string, string> = {
      [ACTIVE_POLICIES]: 'BOUND',
      [RENEWAL_POLICIES]: 'RENEWAL',
      [CANCELLED_POLICIES]: 'CANCELLED',
      [COOLING_OFF_POLICIES]: 'COOLING OFF'
    };

    return Promise.all(
      policies.map((policy) => this.getPolicyCountByState(map[policy]))
    );
  }

  async offPlatformPoliciesStore(payload: IOffPlatformPoliciesStoreRequest) {
    return await this.client.post('/off-platform-policy', payload);
  }

  async offPlatformPoliciesUpdate(
    id: string | number,
    payload: IOffPlatformPoliciesStoreRequest
  ) {
    return await this.client.put(`/off-platform-policy/${id}`, payload);
  }

  offPlatformPoliciesStatistics(): AxiosPromise<{
    Bound: number;
    Cancelled: number;
    'Cooling Off': number;
    Declined: number;
    Expired: number;
    Quote: number;
    Referred: number;
    Renewal: number;
  }> {
    return this.client.get('/off-platform-policy?count=true');
  }

  async offPlatformPoliciesList(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<OffPlatformPolicyInterface>> {
    if (searchTerm) {
      if (!listData.filters) listData.filters = {};
      listData.filters.policy_number = searchTerm;
    }
    const { data } = await this.client.getList(
      '/off-platform-policy',
      listData
    );

    return {
      items: data.data,
      pagination: {
        page: data.meta.current_page,
        perPage: data.meta.per_page,
        total: data.meta.total
      }
    };
  }

  async offPlatformPoliciesDelete(payload: any[]) {
    let policyIds: string = payload.reduce(
      (cur, item) => `${cur}${item.id},`,
      ''
    );
    policyIds = policyIds.substring(0, policyIds.length - 1);
    return await this.client.delete(
      `/off-platform-policy?off_platform_policy_ids=${policyIds}`
    );
  }

  offPlatformPoliciesDetails(
    policyIds: string | number
  ): AxiosPromise<OffPlatformPolicyInterface> {
    return this.client.get(`/off-platform-policy/${policyIds}`);
  }

  offPlatformPoliciesValidate(
    payload: Record<string, unknown>,
    pageKey: string
  ) {
    return this.client.post(
      '/off-platform-policy/validate?stage=' + pageKey,
      payload
    );
  }
  async cancellationReasons(): Promise<cancellationReasonInterface[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/policy-cancellation-reason')
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async exportZip(policyId: string): Promise<string> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/export-zip/invoice/${policyId}`, {
          responseType: 'blob',
          headers: { Accept: 'application/zip' }
        })
        .then((res) => {
          if (res.data) {
            resolve(
              URL.createObjectURL(
                new Blob([res.data], { type: 'application/zip' })
              )
            );
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getPolicyDetails(uuid: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/policies/${uuid}/details`)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }
}

export { policyQueryKeys } from './query-keys';
export type {
  PolicyListQueryParams,
  PolicyOffPlatformListQueryParams
} from './query-keys';
export {
  usePolicyCancellationReasonsQuery,
  usePolicyDetailsQuery,
  usePolicyExportZipQuery,
  usePolicyInvoicesQuery,
  usePolicyListQuery,
  usePolicyOffPlatformDetailQuery,
  usePolicyOffPlatformListQuery,
  usePolicyOffPlatformStatisticsQuery,
  usePolicyQuery,
  usePolicyStatisticsQuery
} from './use-policy-queries';
export {
  usePolicyDeleteMutation,
  usePolicyOffPlatformDeleteMutation,
  usePolicyOffPlatformStoreMutation,
  usePolicyOffPlatformUpdateMutation,
  usePolicyOffPlatformValidateMutation,
  usePolicySendRenewalEmailMutation,
  usePolicySendWelcomeEmailMutation,
  usePolicyUpdatePaidStatusMutation
} from './use-policy-mutations';
export type {
  PolicyDeleteVariables,
  PolicyOffPlatformUpdateVariables,
  PolicyOffPlatformValidateVariables,
  PolicyUpdatePaidStatusVariables
} from './use-policy-mutations';
